<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "What am I posting, and does it look right before it commits?"
  Answer:   the grid itself - the amount column is the heaviest weight in the row (tabular,
            right-aligned), and the post button is the one --color-action control on the
            screen. Nothing here animates except the "Posted" moment on a row that just
            committed and the per-account delta once it has a real value.
  Context:  the kind tabs, the "This batch" footer's ready/needs-fix counts and per-account
            running delta with its "warn, never block" hint, and "Already in the ledger" - the
            latest posted entries, shown only to a role that may read the journal.
  Controls: the kind tabs switch which draft is shown (each kind keeps its own rows), and
            "Post N entries", in the panel, is the only primary action; every row's own
            "+ split" and "×" are secondary, neutral controls. A row a module recognises
            gets its fields in a detail row beneath it (a side panel for a car event), and
            the batch waits until every touched row is ready.
-->
<script setup lang="ts">
import { KindIcon } from '@taler/ui'
import { systemClock, todayUtc } from '@taler/domain'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  canCreateEntries,
  COUNTER_CLASSES_BY_KIND,
  FlowEntryGrid,
  GenericPostingGrid,
  MONEY_CLASSES,
  useEntryBatch,
  useEntryMerchants,
  useEntryTags,
  useEntryTargets,
  useFxTransferExtension,
  useGenericEntry,
  useOtherLedgerAccounts,
  type FlowKind,
  type RegisteredExtension,
  type Target,
} from '@/features/entries'
import { useCarEventExtension } from '@/features/garage'
import { useLoanPaymentExtension } from '@/features/loans'
import { usePrepaidScheduleExtension } from '@/features/prepaid-recognitions'
import { canViewTransactions, RecentTransactions, useTransactions } from '@/features/transactions'
import { useCrossLedgerMovementExtension } from '@/features/transfers'
import { useSession } from '@/app/session'
import { useCollapsedSidebar } from '@/app/shell/sidebar'

const { t, locale } = useI18n()
const session = useSession()
useCollapsedSidebar()

const canCreate = computed(() => canCreateEntries(session.can))
const ledgerId = () => session.activeLedger.value?.id
const activeLocale = () => locale.value as 'en' | 'hu'
const today = () => todayUtc(systemClock)

const merchants = useEntryMerchants({ ledgerId, enabled: () => canCreate.value })
const entryTags = useEntryTags({ ledgerId, enabled: () => canCreate.value })

type Tab = FlowKind | 'OTHER'
const TABS: readonly Tab[] = ['EXPENSE', 'INCOME', 'TRANSFER', 'OTHER']
const activeTab = ref<Tab>('EXPENSE')

// In priority order: the first extension that recognises a row owns it.
const extensions: readonly RegisteredExtension[] = [
  useCrossLedgerMovementExtension({ ledgerId, can: session.can }),
  useLoanPaymentExtension({ ledgerId, can: session.can }),
  useCarEventExtension({ ledgerId, can: session.can }),
  usePrepaidScheduleExtension({ ledgerId, can: session.can, locale: activeLocale }),
  useFxTransferExtension(),
]

const targets = useEntryTargets({
  ledgerId,
  locale: activeLocale,
  enabled: () => canCreate.value,
  canCreateContact: () => session.can('RECORDS_MANAGE'),
  owns: (target) => extensions.some((extension) => extension.owns?.(target) === true),
})

// Only EXPENSE's payment account and TRANSFER's two money accounts may cross a ledger
// boundary (AGENTS.md: the category stays home-ledger-only); INCOME never offers one.
const foreignMoney = useOtherLedgerAccounts({
  activeLedgerId: ledgerId,
  classes: () => MONEY_CLASSES,
  locale: activeLocale,
  enabled: () => canCreate.value && session.can('TRANSFER'),
})

// A foreign account's id can coincide with a home one (the mock seeds every ledger's
// catalogue identically), so it is never merged into this id-keyed map - the cross-ledger
// extension reads a row's chosen ledger off the row itself instead (see its own composable).
const targetsById = computed(() => targets.targetsById.value)

function moneyTargetsFor(kind: FlowKind): readonly Target[] {
  return kind === 'INCOME'
    ? targets.moneyTargets.value
    : [...targets.moneyTargets.value, ...foreignMoney.targets.value]
}

function counterTargetsFor(kind: FlowKind): readonly Target[] {
  const own = targets.counterTargets(COUNTER_CLASSES_BY_KIND[kind])
  return kind === 'TRANSFER' ? [...own, ...foreignMoney.targets.value] : own
}

function optionHint(target: Target): string | undefined {
  for (const extension of extensions) {
    const hint = extension.optionHint?.(target)
    if (hint !== undefined) return hint
  }
  return undefined
}

function batchFor(kind: FlowKind) {
  return useEntryBatch({
    ledgerId,
    kind: () => kind,
    targetsById: () => targetsById.value,
    foreignTargets: () => foreignMoney.targets.value,
    today,
    extensions: () => extensions,
  })
}

const batches: Readonly<Record<FlowKind, ReturnType<typeof useEntryBatch>>> = {
  EXPENSE: batchFor('EXPENSE'),
  INCOME: batchFor('INCOME'),
  TRANSFER: batchFor('TRANSFER'),
}

const generic = useGenericEntry({ ledgerId, targetsById: () => targetsById.value, today })

function tabLabel(tab: Tab): string {
  return t(`entries.tab.${tab.toLowerCase()}`)
}

const canViewRecent = computed(() => canCreate.value && canViewTransactions(session.can))
const recent = useTransactions({
  ledgerId,
  locale: activeLocale,
  enabled: () => canViewRecent.value,
  filters: () => ({}),
})
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-[var(--space-block)] p-[var(--space-page)]">
    <div class="flex flex-col gap-[var(--space-hair)]">
      <RouterLink
        :to="{ name: 'transactions' }"
        :aria-label="t('entries.backToTransactions')"
        class="inline-flex w-fit items-center gap-[var(--space-tight)] text-[length:var(--text-step-2)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
      >
        <span aria-hidden="true">←</span>
        {{ t('transactions.title') }}
      </RouterLink>
      <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
        {{ t('entries.title') }}
      </h1>
    </div>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>
    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('entries.noLedger') }}
    </p>
    <p v-else-if="!canCreate" class="text-[color:var(--color-text-muted)]">
      {{ t('entries.noAccess') }}
    </p>

    <template v-else>
      <div
        role="tablist"
        :aria-label="t('entries.title')"
        class="flex w-fit gap-[var(--space-hair)] rounded-[var(--radius-control)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] p-[var(--space-hair)]"
      >
        <button
          v-for="tab in TABS"
          :key="tab"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab"
          class="flex min-h-[var(--density-target-min)] items-center gap-[var(--space-inline)] rounded-[var(--radius-control)] px-[var(--space-cell)] text-[length:var(--text-step-3)] font-medium transition-colors duration-[var(--motion-fast)]"
          :class="
            activeTab === tab
              ? 'bg-[color:var(--color-surface-sunken)] text-[color:var(--color-text)]'
              : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]'
          "
          @click="activeTab = tab"
        >
          <span aria-hidden="true"><KindIcon :kind="tab === 'OTHER' ? undefined : tab" label="" /></span>
          {{ tabLabel(tab) }}
        </button>
      </div>

      <FlowEntryGrid
        v-for="kind in ['EXPENSE', 'INCOME', 'TRANSFER'] as const"
        v-show="activeTab === kind"
        :key="kind"
        :kind="kind"
        :rows="batches[kind].rows.value"
        :money-targets="moneyTargetsFor(kind)"
        :counter-targets="counterTargetsFor(kind)"
        :targets-by-id="targetsById"
        :frecency="targets.frecency.value"
        :merchants="merchants.merchants.value"
        :merchant-frecency="merchants.frecency.value"
        :tag-suggestions="entryTags.tags.value"
        :running-deltas="batches[kind].runningDeltas.value"
        :views="batches[kind].views.value"
        :resolutions="batches[kind].resolutions.value"
        :outcomes="batches[kind].outcomes"
        :counts="batches[kind].counts.value"
        :can-submit="batches[kind].canSubmit.value"
        :option-hint="optionHint"
        :is-submitting="batches[kind].isSubmitting.value"
        :has-submit-error="batches[kind].submitError.value !== null"
        :submit-error-detail="batches[kind].submitErrorDetail.value"
        :just-posted="batches[kind].justPosted.value"
        @add-row="batches[kind].addRow()"
        @add-split="(rowId) => batches[kind].addSplit(rowId)"
        @remove-split="(rowId, splitId) => batches[kind].removeSplit(rowId, splitId)"
        @remove-row="(rowId) => batches[kind].removeRow(rowId)"
        @commit-row="(rowId) => batches[kind].commitRow(rowId)"
        @update-draft="(rowId, draft) => batches[kind].setExtensionDraft(rowId, draft)"
        @retry-attach="(rowId) => batches[kind].retryAttach(rowId)"
        @submit="batches[kind].submit()"
      >
        <template #aside>
          <RecentTransactions
            v-if="canViewRecent"
            :rows="recent.rows.value"
            :is-loading="recent.isLoading.value"
            :error="recent.error.value"
          />
        </template>
      </FlowEntryGrid>

      <GenericPostingGrid
        v-show="activeTab === 'OTHER'"
        :date="generic.entry.value.date"
        :memo="generic.entry.value.memo"
        :tags="generic.entry.value.tags"
        :tag-suggestions="entryTags.tags.value"
        :lines="generic.entry.value.lines"
        :all-targets="targets.allTargets.value"
        :frecency="targets.frecency.value"
        :is-balanced="generic.validation.value.isBalanced"
        :is-complete="generic.validation.value.isComplete"
        :balance="generic.validation.value.balance"
        :is-submitting="generic.isSubmitting.value"
        :has-submit-error="generic.submitError.value !== null"
        :submit-error-detail="generic.submitErrorDetail.value"
        :just-posted="generic.justPosted.value"
        @update:date="(date) => (generic.entry.value.date = date)"
        @update:memo="(memo) => (generic.entry.value.memo = memo)"
        @update:tags="(tags) => (generic.entry.value.tags = [...tags])"
        @add-line="generic.addLine()"
        @remove-line="(lineId) => generic.removeLine(lineId)"
        @submit="generic.submit()"
      >
        <template #aside>
          <RecentTransactions
            v-if="canViewRecent"
            :rows="recent.rows.value"
            :is-loading="recent.isLoading.value"
            :error="recent.error.value"
          />
        </template>
      </GenericPostingGrid>
    </template>
  </main>
</template>
